import { Address } from '@klear/address'
import type { OrganizationSize } from '@klear/constants'
import type { CountryCode } from '@klear/constants/countries'
import { Timestamp } from '@klear/time'

export type SupplierIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export function supplierToJson(supplier: Supplier) {
  return {
    identity: supplier.identity,
    traderId: supplier.traderId,
    name: supplier.name,
    description: supplier.description,
    address: supplier.address.toJSON(),
    size: supplier.size,
    website: supplier.website,
    dunsNumber: supplier.dunsNumber,
    lei: supplier.lei,
    ein: supplier.ein,
    vatNumber: supplier.vatNumber,
    parentOrganization: supplier.parentOrganization,
    createdAt: supplier.createdAt.epochMs,
    updatedAt: supplier.updatedAt.epochMs,
    deletedAt: supplier.deletedAt?.epochMs,
    createdByUserId: supplier.createdByUserId,
    updatedByUserId: supplier.updatedByUserId,
    deletedByUserId: supplier.deletedByUserId,
    organizationId: supplier.organizationId
  }
}
export type SupplierJson = ReturnType<typeof supplierToJson>

export class Supplier {
  static readonly __klearClass = 'Supplier'

  readonly identity: SupplierIdentity
  traderId: number
  name: string
  description: string
  address: Address
  size?: OrganizationSize
  website?: string
  dunsNumber?: string
  lei?: string
  ein?: string
  vatNumber?: string
  parentOrganization?: string
  createdAt: Timestamp
  updatedAt: Timestamp
  deletedAt?: Timestamp
  createdByUserId?: number
  updatedByUserId?: number
  deletedByUserId?: number
  organizationId?: number

  constructor(props: SupplierJson, options?: { validate?: boolean }) {
    const now = Timestamp.now()
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.address = new Address(props.address)
    this.traderId = props.traderId
    this.name = props.name
    this.description = props.description ?? ''
    this.size = props.size
    this.website = props.website
    this.dunsNumber = props.dunsNumber
    this.lei = props.lei
    this.ein = props.ein
    this.vatNumber = props.vatNumber
    this.parentOrganization = props.parentOrganization
    this.createdAt = new Timestamp(props.createdAt ?? now)
    this.updatedAt = props.updatedAt ? new Timestamp(props.updatedAt) : this.createdAt
    this.deletedAt = props.deletedAt ? new Timestamp(props.deletedAt) : undefined
    this.createdByUserId = props.createdByUserId
    this.updatedByUserId = props.updatedByUserId
    this.deletedByUserId = props.deletedByUserId
    this.organizationId = props.organizationId

    if (options?.validate !== false) {
      this.assertValid()
    }
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get corporateId(): string | undefined {
    return this.dunsNumber ?? this.lei ?? this.ein ?? this.vatNumber ?? undefined
  }

  get country(): CountryCode {
    return this.address.country
  }

  set country(value: CountryCode) {
    this.address = this.address.withCountry(value)
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  requireSaved(): asserts this is this & { id: number } {
    if (!this.isSaved) {
      throw new Error('Supplier is not saved')
    }
  }

  assertValid(): void {
    if (!this.traderId) {
      throw new Error('Supplier trader is required')
    }
    if (!this.name.trim()) {
      throw new Error('Supplier name is required')
    }
  }

  toJSON() {
    return supplierToJson(this)
  }
}
